import React, {
  useRef,
  useState,
  useEffect,
  useCallback,
  useMemo,
  Component,
} from 'react'
import classNames from 'classnames'
import classNamesBind from 'classnames/bind'
import { Dropdown } from '@adc/ui-core'
import { NavigationDropdownProps } from './types'
import NavigationMenuItem from './NavigationMenuItem'
import { getVisibleMenuLinks } from './constants'
import styles from './styles.scss'
import headerStyles from '../styles.scss'

const cx = classNamesBind.bind(styles)
const hcx = classNamesBind.bind(headerStyles)

// Type for Semantic UI Dropdown component instance
// Semantic UI React doesn't provide proper TypeScript types,
// so we use Component<any> and add the specific methods we need
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type DropdownRef = Component<any> & {
  ref?: {
    current?: HTMLElement
  }
  close?: () => void
}

const NavigationDropdown: React.FC<NavigationDropdownProps> = ({
  user,
  menuItems,
  bellNotificationsCount,
  dropdownStyle = 'primary-button',
  className,
  elmId = 'nav_dropdown',
  showIndicator = false,
  indicatorsEnabled = false,
  visibleHeaderLinks,
}) => {
  const dropdownRef = useRef<DropdownRef>(null)
  const [isOpen, setIsOpen] = useState(false)
  const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout>>()

  const handleMenuItemClick = useCallback(() => {
    setIsOpen(false)
    if (dropdownRef.current?.close) {
      dropdownRef.current.close()
    }
  }, [])

  const visibleDropdownLinks = useMemo(
    () => getVisibleMenuLinks(menuItems),
    [menuItems]
  )

  const trackingContext = useMemo(
    () => ({
      linkSource: 'dropdown' as const,
      visibleHeaderLinks: visibleHeaderLinks ?? [],
      visibleDropdownLinks,
    }),
    [visibleHeaderLinks, visibleDropdownLinks]
  )

  const formattedNotificationCount = useMemo(() => {
    if (!bellNotificationsCount) return ''

    const count =
      typeof bellNotificationsCount === 'string'
        ? parseInt(bellNotificationsCount, 10)
        : bellNotificationsCount

    if (!count || isNaN(count) || count <= 0) return ''
    return count >= 20 ? '20+' : String(count)
  }, [bellNotificationsCount])

  // Handle clicking outside to close dropdown
  useEffect(() => {
    if (!isOpen) return

    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current) {
        const dropdownElement = dropdownRef.current.ref?.current
        if (
          dropdownElement &&
          !dropdownElement.contains(event.target as Node)
        ) {
          setIsOpen(false)
          // Reset Semantic UI's internal state
          if (dropdownRef.current.close) {
            dropdownRef.current.close()
          }
        }
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  const handleMouseEnter = useCallback(() => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current)
      hoverTimeoutRef.current = undefined
    }
    setIsOpen(true)
  }, [])

  const handleMouseLeave = useCallback(() => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current)
    }
    hoverTimeoutRef.current = setTimeout(() => {
      setIsOpen(false)
      if (dropdownRef.current?.close) {
        dropdownRef.current.close()
      }
    }, 300)
  }, [])

  const handleButtonClick = useCallback((e: React.MouseEvent) => {
    const target = e.target as HTMLElement
    if (!target.closest('.menu-item')) {
      e.preventDefault()
      e.stopPropagation()
    }
  }, [])

  // The redesigned feed menu (featured card present) renders wider than the
  // legacy 198px item list; keyed off content so the flag never reaches here.
  const hasFeaturedCard = useMemo(
    () => menuItems.some(item => item.isFeaturedCard),
    [menuItems]
  )

  return (
    <div
      className={cx('navigation-dropdown-container')}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <Dropdown
        ref={dropdownRef}
        item
        simple
        floating
        open={isOpen}
        onClick={handleButtonClick}
        onMouseDown={(e: React.MouseEvent) => e.preventDefault()}
        selectOnBlur={false}
        selectOnNavigation={false}
        // @ts-ignore - Semantic UI accepts JSX for text prop despite typing
        text={
          user.logged_in ? (
            <span className={hcx('header-truncate')}>
              {indicatorsEnabled
                ? showIndicator && (
                    <span
                      className={hcx('nav-account-indicator-dot')}
                      data-elm-id='nav_account_indicator'
                    />
                  )
                : formattedNotificationCount && (
                    <span className={hcx('bell-notification', 'top-left')}>
                      {formattedNotificationCount}
                    </span>
                  )}{' '}
              Welcome, {user.first_name || 'User'}
            </span>
          ) : (
            <span className={hcx('header-truncate')}>Menu</span>
          )
        }
        direction='left'
        className={classNames(
          cx('navigation-dropdown'),
          hcx(dropdownStyle),
          className
        )}
        data-elm-id={elmId}
        aria-label='User menu dropdown'
      >
        <Dropdown.Menu
          className={cx('dropdown-menu', { 'feed-menu': hasFeaturedCard })}
        >
          {menuItems.map(item => (
            <NavigationMenuItem
              key={item.id}
              item={item}
              onMenuItemClick={handleMenuItemClick}
              bellNotificationsCount={formattedNotificationCount}
              indicatorsEnabled={indicatorsEnabled}
              trackingContext={trackingContext}
            />
          ))}
        </Dropdown.Menu>
      </Dropdown>
    </div>
  )
}

export default NavigationDropdown
